import { useState } from 'react';

import { useMutateCheckPresetForCreate } from '@hooks/kpis/useMutateCheckPresetForCreate';
import { useMutateCheckPresetForUpdate } from '@hooks/kpis/useMutateCheckPresetForUpdate';
import { useMutateExportCompareKpiCharts } from '@hooks/kpis/useMutateExportCompareKpiCharts';
import { useMutateExportCorrelateKpiCharts } from '@hooks/kpis/useMutateExportCorrelateKpiCharts';
import { useMutateExportKpiCharts } from '@hooks/kpis/useMutateExportKpiCharts';
import { useMutateExportKpiChartsFromParams } from '@hooks/kpis/useMutateExportKpiChartsFromParams';
import { useMutateKpiCharts } from '@hooks/kpis/useMutateKpiCharts';
import { useMutateRemovePreset } from '@hooks/kpis/useMutateRemovePreset';
import { useMutateRunCorrelation } from '@hooks/kpis/useMutateRunCorrelation';
import { useMutateSavePreset } from '@hooks/kpis/useMutateSavePreset';
import { useMutateSetKpiVendor } from '@hooks/kpis/useMutateSetKpiVendor';
import { useMutateUpdatePreset } from '@hooks/kpis/useMutateUpdatePreset';
import { useMutateUpdatePresetName } from '@hooks/kpis/useMutateUpdatePresetName';
import { useQueryKpiVendors } from '@hooks/kpis/useQueryKpiVendors';
import { useQueryOptionsCellsLimenet } from '@hooks/kpis/useQueryOptionsCellsLimenet';
import { useQueryOptionsCellsNokia } from '@hooks/kpis/useQueryOptionsCellsNokia';
import { useQueryOptionsKpi } from '@hooks/kpis/useQueryOptionsKpi';
import { useQueryOptionsKpiLineType } from '@hooks/kpis/useQueryOptionsKpiLineType';
import { useQueryPresets } from '@hooks/kpis/useQueryPresets';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { IChart } from '@interfaces/kpi/kpiCommon';

import { EVendorsValues } from '@enums/common';

import {
	OPTIONS_LIMENET_KPI,
	OPTIONS_LIMENET_KPI_LINE_TYPE,
	OPTIONS_NOKIA_KPI,
	OPTIONS_NOKIA_KPI_LINE_TYPE,
} from '@constants/queryKeys';

import { useQueryOptionsCellsOran } from './useQueryOptionsCellsOran';

interface UseKpiProps {
	isKpiView?: boolean;
	isOranVendor: boolean;
}

export const useKPI = (props?: UseKpiProps) => {
		const { isKpiView = true, isOranVendor = false } = props || {};
		const { presets } = useQueryPresets({ isKpiView });
		const { availableVendors } = useAppSelector(auth);

		const [errorTextKpi, setErrorTextKpi] = useState<string>();

		const {
			options4GTddLimenet,
			isLoadingOptions4GTddLimenet,
			options4GFddLimenet,
			isLoadingOptions4GFddLimenet,
			options5GLimenet,
			isLoadingOptions5GLimenet,
		} = useQueryOptionsCellsLimenet({ isKpiView, availableVendors });

		const {
			options4GTddNokia,
			isLoadingOptions4GTddNokia,
			options4GFddNokia,
			isLoadingOptions4GFddNokia,
			options5GNokia,
			isLoadingOptions5GNokia,
		} = useQueryOptionsCellsNokia({ isKpiView, availableVendors });

		const {
			options5GOran,
			isLoadingOptions5GOran,
		} = useQueryOptionsCellsOran({ isKpiView, availableVendors });

		const {
			optionsKpi: optionsLimenetKpi,
			isLoadingOptionsKpi: isLoadingOptionsLimenetKpi,
		} = useQueryOptionsKpi({
			isKpiView,
			vendor: EVendorsValues.limenet,
			queryKey: OPTIONS_LIMENET_KPI,
			availableVendors,
		});

		const {
			optionsKpi: optionsNokiaKpi,
			isLoadingOptionsKpi: isLoadingOptionsNokiaKpi,
		} = useQueryOptionsKpi({
			isKpiView,
			vendor: EVendorsValues.nokia,
			queryKey: OPTIONS_NOKIA_KPI,
			availableVendors,
		});

		const {
			optionsKpi: optionsLimenetKpiLineType,
			isLoadingOptionsKpi: isLoadingOptionsLimenetKpiLineType,
		} = useQueryOptionsKpiLineType({
			isKpiView,
			vendor: EVendorsValues.limenet,
			queryKey: OPTIONS_LIMENET_KPI_LINE_TYPE,
			availableVendors,
		});

		const {
			optionsKpi: optionsNokiaKpiLineType,
			isLoadingOptionsKpi: isLoadingOptionsNokiaKpiLineType,
		} = useQueryOptionsKpiLineType({
			isKpiView,
			vendor: EVendorsValues.nokia,
			queryKey: OPTIONS_NOKIA_KPI_LINE_TYPE,
			availableVendors,
		});

		const { optionsVendorsKpi, isLoadingVendorsKpi } = useQueryKpiVendors(isKpiView);

		const {
			compareDrillDownCharts,
			dataCharts,
			dataDrillDownCharts,
			getCharts,
			getDrillDownCharts,
			getDrillDownCompareCharts,
			isLoadingCharts,
			isLoadingDrillDownCharts,
			isLoadingDrillDownCompareCharts,
			runProgress,
			progress,
		} = useMutateKpiCharts(setErrorTextKpi);

		const { deletePreset, isLoadingDeletePreset } = useMutateRemovePreset();
		const { setDefaultKpiVendor, isLoadingSetKpiVendor } = useMutateSetKpiVendor();
		const { handleUpdatePresetName, isLoadingUpdatePresetName } = useMutateUpdatePresetName();
		const { handleUpdatePreset, isLoadingUpdatePreset } = useMutateUpdatePreset();
		const { createPreset, isLoadingCreatePreset } = useMutateSavePreset();
		const { handleCheckUpdatePreset, isLoadingCheckUpdatePreset } =
			useMutateCheckPresetForUpdate({ handleUpdatePresetName, isOranVendor });
		const { handleCheckCreatePreset, isLoadingCheckCreatePreset } = useMutateCheckPresetForCreate({
			createPreset,
			isOranVendor,
		});
		const { exportCharts } = useMutateExportKpiCharts();
		const { exportChartsFromParams } = useMutateExportKpiChartsFromParams();
		const { exportCompareCharts } = useMutateExportCompareKpiCharts();
		const { exportCorrelateCharts } = useMutateExportCorrelateKpiCharts();
		const { correlationData, isLoadingCorrelation, getCorrelation } = useMutateRunCorrelation();

		return {
			isLoadingOptions: isLoadingOptions4GTddLimenet
				|| isLoadingOptions4GTddNokia
				|| isLoadingOptions4GFddLimenet
				|| isLoadingOptions4GFddNokia
				|| isLoadingOptions5GLimenet
				|| isLoadingOptions5GNokia
				|| isLoadingOptions5GOran
				|| isLoadingOptionsLimenetKpi
				|| isLoadingOptionsNokiaKpi
				|| isLoadingSetKpiVendor
				|| isLoadingVendorsKpi
				|| isLoadingOptionsNokiaKpiLineType
				|| isLoadingOptionsLimenetKpiLineType,
			isLoadingPresetForm: isLoadingCheckUpdatePreset
				|| isLoadingCheckCreatePreset
				|| isLoadingUpdatePresetName
				|| isLoadingUpdatePreset
				|| isLoadingCreatePreset
				|| isLoadingDeletePreset,
			isLoadingCharts,
			isLoadingCorrelation,
			handleCheckCreatePreset,
			handleCheckUpdatePreset,
			handleUpdatePreset,
			isLoadingDrillDownCharts,
			isLoadingDrillDownCompareCharts,
			optionsVendorsKpi: optionsVendorsKpi || [],
			presets: presets || [],
			charts: dataCharts?.charts || [],
			drillDownCharts: dataDrillDownCharts?.charts || [],
			compareDrillDownCharts: compareDrillDownCharts?.map((data: { charts: Array<IChart> }) =>
				data?.charts[0]) || [],
			options5GOran: options5GOran || [],
			options4GTddLimenet: options4GTddLimenet || [],
			options4GTddNokia: options4GTddNokia || [],
			options4GFddLimenet: options4GFddLimenet || [],
			options4GFddNokia: options4GFddNokia || [],
			options5GLimenet: options5GLimenet || [],
			options5GNokia: options5GNokia || [],
			optionsLimenetKpi: optionsLimenetKpi || [],
			optionsNokiaKpi: optionsNokiaKpi || [],
			optionsLimenetKpiLineType: optionsLimenetKpiLineType || [],
			optionsNokiaKpiLineType: optionsNokiaKpiLineType || [],
			correlation: correlationData?.correlation,
			getCorrelation,
			getDrillDownCompareCharts,
			getDrillDownCharts,
			getCharts,
			deletePreset,
			setDefaultKpiVendor,
			exportCharts,
			exportChartsFromParams,
			exportCompareCharts,
			exportCorrelateCharts,
			errorTextKpi,
			runProgress,
			progress,
		};
	}
;
